import React, { useEffect, useState } from 'react';
import { 
  Shield, Activity, AlertTriangle, Database, Play, Clock, 
  ChevronRight, ChevronDown, Check, Layers, AlertCircle, Cloud, 
  Terminal, Lock, Server, Cpu, TrendingUp, TrendingDown, Zap
} from 'lucide-react';
import './SOCDashboard.css';

const SOCDashboard = ({ data }) => {
  const [currentLogIndex, setCurrentLogIndex] = useState(0);
  const [isExecuting, setIsExecuting] = useState(false);
  const [logsVisible, setLogsVisible] = useState(true);
  const [expandedRow, setExpandedRow] = useState(0);

  // SAFEGUARD: Extract data
  const socData = data.socDashboard || data;

  // --- 1. HARDCODED SKILL DATA ---
  
  // VAPT & Vuln Mgmt Tools
  const vaptTools = [
    { type: "Burp Suite Pro", count: 95 }, 
    { type: "Nessus / Tenable", count: 85 }, 
    { type: "Metasploit", count: 70 },       
    { type: "Wireshark", count: 80 },        
    { type: "Rapid7 InsightVM", count: 75 }, 
    { type: "ThreatWorx", count: 65 }        
  ];

  // AppSec Pipeline
  const appSecPipeline = [
    { stage: "SAST / CODE", tool: "CodeQL", status: "Active" },
    { stage: "DAST / BUILD", tool: "Veracode / Rapid7", status: "Scanning" },
    { stage: "FUZZING", tool: "Burp Suite", status: "Verified" },
    { stage: "DEPLOY / OPS", tool: "OWASP ZAP", status: "Monitored" }
  ];

  // Cloud & Compliance
  const complianceData = [
    { name: "Azure Sentinel", score: 92 },
    { name: "MS Defender for Cloud", score: 88 },
    { name: "MS Defender for Endpoint", score: 95 },
    { name: "HIPAA Compliance", score: 98 },
    { name: "CIS Benchmarks", score: 85 }
  ];

  const complianceStatus = [
    { label: "Compliant", value: 85, color: "#00FF41" },
    { label: "Review", value: 10, color: "#FFB300" },
    { label: "Critical", value: 5, color: "#D32F2F" }
  ];

  // --- UPDATED JOB DETAILS ---
  const jobDetails = [
    {
      company: "Wipro",
      role: "Security Engineer",
      period: "Dec 2025 - Present",
      location: "Bengaluru, India",
      locationType: "In-Office", // NEW FIELD
      points: [
        "Continued as a Security Analyst on Wipro-managed MSSP engagements, owning 24×7 security operations for enterprise and healthcare workloads.",
        "Supported 3 mission-critical healthcare applications, implementing HIPAA-aligned application security controls for customer-facing services.",
        "Executed automated SAST and DAST using CodeQL across 3+ application builds, identifying, prioritizing, and driving remediation of high-risk vulnerabilities in collaboration with development teams.",
        "Conducted RCA, malware analysis, and log investigation on 50+ security alerts using Microsoft Defender, delivering actionable findings, remediation guidance, and coordinating with cross-functional stakeholders to reduce incident recurrence and response time."
      ]
    },
    {
      company: "HARMAN India",
      role: "SOC Analyst",
      period: "Aug 2024 - Present",
      location: "Bengaluru, India",
      locationType: "Hybrid", // NEW FIELD
      points: [
        "Secured enterprise projects with 1,200–3,000+ user accounts, reducing exposure to threats through proactive monitoring.",
        "Led daily SOC operations, enabling faster threat detection and response — improving incident resolution time by 30%.",
        "Investigated 100+ security alerts weekly, preventing potential breaches and strengthening the company’s cyber resilience.",
        "Conducted threat investigations and incident triage, strengthening situational awareness and reducing analyst response times by 20%.",
        "Prepared and shared daily and monthly vulnerability reports, highlighting key findings, trends, and risk ratings to stakeholders and clients for proactive remediation.",
        "Collaborated directly with clients and internal teams to communicate risk status, provide actionable insights, and ensure the timely closure of identified vulnerabilities.",
        "Created and maintained comprehensive incident response documentation, ensuring consistent and efficient operational procedures.",
        "Managed email security operations, focusing on phishing threat detection, analysis, and incident handling, minimizing social engineering risks.",
        "Performed Vulnerability Assessment and Penetration Testing (VAPT) and Vulnerability Management on critical infrastructure, identifying and remediating high-risk vulnerabilities to minimize attack surface.",
        "Conducted manual and automated application security assessments using SAST and DAST on customer-facing services, identifying high-risk vulnerabilities, driving remediation with development teams, improving overall security posture by 20%, and supporting HIPAA compliance requirements.",
        "Contributed to 3 high-impact, mission-critical projects within the healthcare domain, supporting secure delivery of customer-facing applications and EDR."
      ]
    }
  ];

  useEffect(() => {
    if (socData?.siemLogs) {
      const interval = setInterval(() => {
        setCurrentLogIndex((prev) => (prev + 1) % socData.siemLogs.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [socData]);

  const runQuery = () => {
    setIsExecuting(true);
    setLogsVisible(false);
    setTimeout(() => {
      setIsExecuting(false);
      setLogsVisible(true);
    }, 800);
  };

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // --- CHART COMPONENTS ---

  const AreaChart = ({ data }) => {
    if (!data || data.length === 0) return null;
    
    const height = 60;
    const width = 100;
    const max = Math.max(...data.map(d => d.count)) || 1;
    
    const points = data.map((d, i) => {
      const x = (i / (data.length - 1)) * width;
      const y = height - (d.count / max) * height;
      return `${x},${y}`;
    }).join(' ');

    const linePath = `M ${points}`;
    const areaPath = `${linePath} L ${width},${height} L 0,${height} Z`;

    return (
      <div className="chart-container area-container">
        <svg viewBox={`0 0 ${width} ${height}`} className="area-chart" preserveAspectRatio="none">
           <defs>
             <linearGradient id="areaGradient" x1="0" x2="0" y1="0" y2="1">
               <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.5" />
               <stop offset="100%" stopColor="#00F0FF" stopOpacity="0.05" />
             </linearGradient>
           </defs>
           <line x1="0" y1="20" x2="100" y2="20" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
           <line x1="0" y1="40" x2="100" y2="40" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
           
           <path d={areaPath} fill="url(#areaGradient)" />
           <path d={linePath} fill="none" stroke="#00F0FF" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
           
           {data.map((d, i) => {
             const x = (i / (data.length - 1)) * width;
             const y = height - (d.count / max) * height;
             return <circle key={i} cx={x} cy={y} r="1.5" fill="#fff" />
           })}
        </svg>
        
        <div className="area-labels">
          {data.map((d, i) => (
            <span key={i} className="area-label">{d.time}</span>
          ))}
        </div>
      </div>
    );
  };

  const SimplePieChart = ({ data }) => {
    const total = data.reduce((acc, item) => acc + item.value, 0);
    let cumulativePercent = 0;
    return (
      <div className="chart-container pie-container">
        <svg viewBox="0 0 100 100" className="simple-pie">
          {data.map((slice, i) => {
            const percent = slice.value / total;
            const startX = Math.cos(2 * Math.PI * cumulativePercent) * 40 + 50;
            const startY = Math.sin(2 * Math.PI * cumulativePercent) * 40 + 50;
            cumulativePercent += percent;
            const endX = Math.cos(2 * Math.PI * cumulativePercent) * 40 + 50;
            const endY = Math.sin(2 * Math.PI * cumulativePercent) * 40 + 50;
            const largeArc = percent > 0.5 ? 1 : 0;
            const pathData = `M 50 50 L ${startX} ${startY} A 40 40 0 ${largeArc} 1 ${endX} ${endY} Z`;
            return <path key={i} d={pathData} fill={slice.color} />;
          })}
          <circle cx="50" cy="50" r="25" fill="var(--bg-panel)" />
        </svg>
        <div className="pie-legend">
          {data.map((item, i) => (
            <div key={i} className="legend-item">
              <span className="legend-dot" style={{background: item.color}}></span>
              <span className="legend-text">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const HorizontalBarChart = ({ data }) => {
    const max = 100; 
    return (
      <div className="chart-container h-bar-container">
        {data.map((d, i) => (
          <div key={i} className="h-bar-row">
            <span className="h-bar-label">{d.type}</span>
            <div className="h-bar-track">
              <div 
                className="h-bar-fill" 
                style={{width: `${(d.count / max) * 100}%`}}
              ></div>
            </div>
            <span className="h-bar-value">{d.count}%</span>
          </div>
        ))}
      </div>
    );
  };

  if (!socData) return <div className="text-white text-center p-10">Loading SOC Data...</div>;

  return (
    <section className="soc-section" id="soc">
      <div className="soc-container">
        
        {/* Header */}
        <div className="soc-header">
          <div className="soc-title-wrapper">
            <Shield className="soc-icon" size={40} />
            <div>
              <h2 className="section-title soc-title">COMMAND CENTER</h2>
              <p className="soc-subtitle">Operational Metrics & Tool Proficiency</p>
            </div>
          </div>
          <div className="soc-status">
            <div className="status-indicator pulse"></div>
            <span className="status-text">SYSTEM ONLINE</span>
          </div>
        </div>

        {/* --- KQL SECTION (UPDATED) --- */}
        <div className="soc-experience-kql">
          <div className="kql-header-wrapper">
             <Database className="soc-icon" size={24} />
             <h3 className="experience-title">LOG ANALYTICS WORKSPACE // CareerHistory</h3>
          </div>

          <div className="kql-terminal">
            <div className="kql-toolbar">
              <div className="tab active"><span className="tab-icon">📋</span> Professional_Logs.kql</div>
              <div className="toolbar-actions">
                <span className="time-range"><Clock size={12}/> Last 5 Years</span>
                <button className={`run-button ${isExecuting ? 'running' : ''}`} onClick={runQuery}>
                  <Play size={14} fill="currentColor" /> {isExecuting ? 'EXECUTING...' : 'RUN'}
                </button>
              </div>
            </div>
            
            <div className="kql-editor">
              <div className="line-numbers"><span>1</span><span>2</span><span>3</span><span>4</span><span>5</span></div>
              <div className="code-content">
                <div className="code-line"><span className="tbl">CareerHistory</span></div>
                <div className="code-line">| <span className="kwd">where</span> <span className="fld">Category</span> == <span className="str">"Cybersecurity"</span></div>
                <div className="code-line">| <span className="kwd">project</span> <span className="fld">TimeGenerated</span>, <span className="fld">Company</span>, <span className="fld">Role</span>, <span className="fld">LocationType</span></div>
                <div className="code-line">| <span className="kwd">order by</span> <span className="fld">TimeGenerated</span> <span className="kwd">desc</span></div>
                <div className="code-line">| <span className="kwd">take</span> 10</div>
              </div>
            </div>

            <div className="kql-results-panel">
              <div className="results-header">
                <span className="result-label">Results</span>
                <span className="result-meta">
                  {isExecuting ? <span className="status-loading">Loading...</span> : <span className="status-ready"><Check size={12}/> Completed</span>}
                  <span className="record-count">{jobDetails.length} records found</span>
                </span>
              </div>
              {logsVisible && (
                <div className="results-table-container">
                  <table className="kql-table">
                    <thead>
                      <tr>
                        <th className="cell-toggle"></th>
                        <th className="cell-time">TimeGenerated</th>
                        <th className="cell-company">Company</th>
                        <th className="cell-role">Role</th>
                        <th className="cell-location">Location</th>
                        {/* NEW HEADER */}
                        <th className="cell-type">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobDetails.map((job, index) => (
                        <React.Fragment key={index}>
                          <tr className={`log-row ${expandedRow === index ? 'expanded' : ''}`} onClick={() => toggleRow(index)}>
                            <td className="cell-toggle">{expandedRow === index ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}</td>
                            <td className="cell-time">{job.period.split(' ')[0]}...</td>
                            <td className="cell-company">{job.company}</td>
                            <td className="cell-role">{job.role}</td>
                            <td className="cell-location">{job.location.split(',')[0]}</td>
                            {/* NEW CELL */}
                            <td className="cell-type" style={{color: '#FFB300'}}>{job.locationType}</td>
                          </tr>
                          {expandedRow === index && (
                            <tr className="details-row">
                              <td colSpan="6"> {/* Increased colSpan */}
                                <div className="log-details-view">
                                  <div className="json-key-val"><span className="json-key">FullTime:</span> <span className="json-bool">true</span></div>
                                  <div className="json-key-val"><span className="json-key">Duration:</span> <span className="json-str">"{job.period}"</span></div>
                                  <div className="json-key-val"><span className="json-key">Location:</span> <span className="json-str">"{job.location} ({job.locationType})"</span></div>
                                  <div className="json-key-val"><span className="json-key">Responsibilities:</span></div>
                                  <ul className="log-list">
                                    {job.points.map((point, i) => (
                                      <li key={i}><span className="list-bullet">_</span> {point}</li>
                                    ))}
                                  </ul>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {isExecuting && <div className="executing-state"><div className="loading-bar"></div><span>Querying Data...</span></div>}
            </div>
          </div>
        </div>

        {/* --- MAIN DASHBOARD GRID --- */}
        <div className="dashboard-main-grid">
          
          {/* 1. VULN MANAGEMENT */}
          <div className="dashboard-panel offensive-panel">
            <div className="panel-header">
              <h3 className="panel-title">VULNERABILITY & THREAT MGMT</h3>
              <Activity size={16} className="panel-icon" />
            </div>
            <div className="panel-content-col">
              <div className="chart-wrapper-wide">
                 <h4 className="chart-subtitle">Toolkit Proficiency & Usage</h4>
                 <HorizontalBarChart data={vaptTools} />
              </div>
              <div className="threat-feed-container" style={{marginTop: '20px', borderTop: '1px solid var(--border-color)', paddingTop: '15px'}}>
                 <h4 className="chart-subtitle">Active Threat Monitoring</h4>
                 {socData.recentThreats && socData.recentThreats.map((threat, index) => (
                    <div key={index} className="threat-feed-item">
                        <div className="threat-icon-box"><AlertTriangle size={14} color="#FF5722"/></div>
                        <div className="threat-details">
                            <div className="threat-type">{threat.type}</div>
                            <div className="threat-source">{threat.source}</div>
                        </div>
                        <div className="threat-action">
                            <span className="action-badge">{threat.action}</span>
                        </div>
                    </div>
                 ))}
              </div>
            </div>
          </div>

          {/* 2. LIVE SOC OPS */}
          <div className="dashboard-panel defensive-panel">
            <div className="panel-header">
              <h3 className="panel-title">LIVE SOC OPERATIONS</h3>
              <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                <span className="live-badge"><span className="blink-dot"></span> LIVE</span>
                <AlertCircle size={16} className="panel-icon" />
              </div>
            </div>
            <div className="panel-content-col">
               <div className="chart-wrapper-bottom">
                  <h4 className="chart-subtitle">Alert Volume (Last 24h)</h4>
                  {socData.alertVolume && <AreaChart data={socData.alertVolume} />}
               </div>
               <div className="kpi-grid">
                   <div className="kpi-box">
                       <span className="kpi-label">MTTR</span>
                       <div className="kpi-val-row">
                          <span className="kpi-value">15m</span>
                          <span className="trend down"><TrendingDown size={12}/> 2m</span>
                       </div>
                   </div>
                   <div className="kpi-box">
                       <span className="kpi-label">False Positive</span>
                       <div className="kpi-val-row">
                          <span className="kpi-value">12%</span>
                          <span className="trend down"><TrendingDown size={12}/> 1%</span>
                       </div>
                   </div>
                   <div className="kpi-box">
                       <span className="kpi-label">Coverage</span>
                       <div className="kpi-val-row">
                          <span className="kpi-value">24/7</span>
                          <span className="trend stable"><Zap size={12}/></span>
                       </div>
                   </div>
               </div>
            </div>
          </div>

          {/* 3. APPSEC PIPELINE */}
          <div className="dashboard-panel appsec-panel">
            <div className="panel-header">
              <h3 className="panel-title">APPSEC PIPELINE INTEGRATION</h3>
              <Layers size={16} className="panel-icon" />
            </div>
            <div className="panel-content-col">
                <div className="pipeline-container">
                    {appSecPipeline.map((step, index) => (
                        <div key={index} className="pipeline-card">
                            <div className="pipeline-header">
                                <span className="pipeline-stage">{step.stage}</span>
                                <div className={`pipeline-status ${step.status === 'Active' ? 'pulse-green' : ''}`}></div>
                            </div>
                            <div className="pipeline-tool">
                                <Terminal size={14} />
                                <span>{step.tool}</span>
                            </div>
                            {index < appSecPipeline.length - 1 && (
                                <div className="pipeline-connector">
                                    <div className="arrow-line"></div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
          </div>

          {/* 4. CLOUD & COMPLIANCE */}
          <div className="dashboard-panel cloud-panel">
            <div className="panel-header">
              <h3 className="panel-title">CLOUD POSTURE (CSPM)</h3>
              <Cloud size={16} className="panel-icon" />
            </div>
            <div className="panel-content-row">
               <div className="chart-wrapper">
                  <h4 className="chart-subtitle">Policy Status</h4>
                  <SimplePieChart data={complianceStatus} />
               </div>
               <div className="cloud-metrics-container">
                  {complianceData.map((item, index) => (
                      <div key={index} className="cloud-metric-item">
                          <div className="cloud-label-row">
                              <span>{item.name}</span>
                              <span className="cloud-score">{item.score}%</span>
                          </div>
                          <div className="cloud-progress-bg">
                              <div className="cloud-progress-fill" style={{ width: `${item.score}%` }}></div>
                          </div>
                      </div>
                  ))}
               </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default SOCDashboard;